import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  User, FolderOpen, Plus, Clock, FileVideo, Search, Loader2,
  MoreVertical, Pencil, Archive, ArchiveRestore, Trash2, Upload, Radio,
  Scissors, Image as ImageIcon, Sparkles, CalendarClock, LayoutTemplate,
} from 'lucide-react';
import { toast } from 'sonner';
import ProjectFormDemo from '@/components/project-templates/ProjectFormDemo';

const API_URL = window.__BACKEND_URL;
const OVERRIDES_PREFIX = 'consistency_demo_project_overrides_';
const FILTERS = ['active', 'archived', 'all'];

function loadOverrides(creatorId) {
  try { return JSON.parse(localStorage.getItem(`${OVERRIDES_PREFIX}${creatorId}`)) || {}; }
  catch { return {}; }
}
function saveOverrides(creatorId, overrides) {
  localStorage.setItem(`${OVERRIDES_PREFIX}${creatorId}`, JSON.stringify(overrides));
}

// The same 9 Production Tools shown on the Production Hub — icons/order
// kept identical so New Project and the Hub feel like one consistent grid.
const TOOL_DEFS = [
  { key: 'upload', label: 'Upload', icon: Upload },
  { key: 'golive', label: 'Go Live', icon: Radio },
  { key: 'editor', label: 'Editor', icon: Pencil },
  { key: 'clips', label: 'Clips', icon: Scissors },
  { key: 'thumbnails', label: 'Thumbnails', icon: ImageIcon },
  { key: 'optimize', label: 'Optimize', icon: Sparkles },
  { key: 'schedule', label: 'Schedule', icon: CalendarClock },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'media', label: 'Media Library', icon: FolderOpen },
];

export default function Workspace() {
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');
  const [overrides, setOverrides] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const activeId = localStorage.getItem('activeCreatorId');

  const fetchProjects = async () => {
    if (!activeId) return;
    try {
      const res = await fetch(`${API_URL}/api/projects?creator_id=${activeId}`, { credentials: 'include' });
      if (res.ok) {
        setProjects(await res.json());
      } else {
        toast.error('Could not load your projects right now');
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      toast.error('Could not load your projects right now');
    }
  };

  useEffect(() => {
    const fetchCreator = async () => {
      try {
        if (!activeId) return;
        const res = await fetch(`${API_URL}/api/creators`, { credentials: 'include' });
        if (res.ok) {
          const list = await res.json();
          const match = (Array.isArray(list) ? list : []).find((c) => (c.id || c._id) === activeId);
          if (match) setCreator(match);
        }
      } catch (err) {
        console.error('Failed to fetch creator:', err);
      }
    };
    if (activeId) setOverrides(loadOverrides(activeId));
    (async () => {
      setLoading(true);
      await Promise.all([fetchCreator(), fetchProjects()]);
      setLoading(false);
    })();
  }, []);

  const persistOverrides = useCallback((next) => {
    setOverrides(next);
    saveOverrides(activeId, next);
  }, [activeId]);

  const handleOpenProject = (projectId) => {
    navigate(`/app/create/workspace/project/${projectId}`);
  };

  const handleCreateProject = async (toolKey) => {
    if (!newProjectName.trim() || !activeId || creating) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ creator_id: activeId, name: newProjectName.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not create project');
      }
      const project = await res.json();
      setShowCreateDialog(false);
      setNewProjectName('');
      // Create Project (no toolKey) lands on the plain Production Hub.
      // Clicking a Production Tool creates the project the same way, then
      // opens that tool within the new project's context.
      navigate(`/app/create/workspace/project/${project.id}${toolKey ? `?openTool=${toolKey}` : ''}`);
    } catch (err) {
      toast.error(err.message || 'Could not create project');
      // Stay in the form on failure — no navigation.
    } finally {
      setCreating(false);
    }
  };

  const newProjectToolsActions = TOOL_DEFS.map(t => ({
    ...t,
    disabled: !newProjectName.trim() || creating,
    onClick: () => handleCreateProject(t.key),
  }));

  // ── Local-only simulated card actions (no backend project mutation) ──
  const openRename = (p) => { setRenameTarget(p); setRenameValue(overrides[p.id]?.name ?? p.name); };
  const confirmRename = () => {
    if (!renameValue.trim()) return;
    persistOverrides({ ...overrides, [renameTarget.id]: { ...overrides[renameTarget.id], name: renameValue.trim() } });
    toast.success('Renamed locally (demo) — not saved to the server');
    setRenameTarget(null);
  };
  const toggleArchive = (p, archived) => {
    persistOverrides({ ...overrides, [p.id]: { ...overrides[p.id], archived } });
    toast.success(archived ? 'Archived (demo)' : 'Restored (demo)');
  };
  const confirmDelete = () => {
    persistOverrides({ ...overrides, [deleteTarget.id]: { ...overrides[deleteTarget.id], deleted: true } });
    toast.success('Deleted locally (demo) — not removed from the server');
    setDeleteTarget(null);
  };

  const displayProjects = projects
    .map(p => ({ ...p, name: overrides[p.id]?.name || p.name, archived: !!overrides[p.id]?.archived, deleted: !!overrides[p.id]?.deleted }))
    .filter(p => !p.deleted)
    .filter(p => filter === 'all' ? true : filter === 'archived' ? p.archived : !p.archived)
    .filter(p => p.name?.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Breadcrumb */}
      <div className="mb-4" data-testid="workspace-breadcrumb">
        <p className="text-xs text-[#8A93A6] tracking-wide">
          <Link to="/app/create" className="hover:text-white transition-colors">Create</Link>
          <span className="mx-1.5 opacity-50">/</span>
          <span className="text-[#C0C7D4]">Workspace</span>
        </p>
      </div>

      {/* Page Title */}
      <h1 className="text-3xl font-bold text-white mb-5" data-testid="workspace-title">Workspace</h1>

      {/* Creator Context */}
      <div className="flex items-center gap-3 mb-8" data-testid="workspace-creator-context">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shrink-0">
          <User className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="text-base font-semibold text-white uppercase tracking-wide">
            {creator?.name || 'Creator'}
          </p>
          <p className="text-xs text-[#8A93A6]">
            {creator?.handle || creator?.username || '@creator'}
          </p>
        </div>
      </div>

      {/* Project Grid */}
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-white">Projects</h2>
          <div className="flex items-center gap-1 bg-white/[0.03] border border-white/10 rounded-lg p-0.5" data-testid="project-filter-group">
            {FILTERS.map(f => (
              <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 rounded-md text-xs capitalize transition-colors ${filter === f ? 'bg-[#3A7BFF] text-white' : 'text-[#8A93A6] hover:text-white'}`} data-testid={`project-filter-${f}`}>
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-[#8A93A6] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects..."
              className="pl-8 h-9 w-56 bg-white/5 border-white/10 text-white text-sm"
              data-testid="project-search-input"
            />
          </div>
          <Button
            variant="outline"
            className="border-white/10"
            onClick={() => setShowCreateDialog(true)}
            data-testid="new-project-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="workspace-loading">
          <Loader2 className="h-6 w-6 text-[#8A93A6] animate-spin" />
        </div>
      ) : displayProjects.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-xl" data-testid="workspace-empty-state">
          <FolderOpen className="h-8 w-8 text-[#555E6E] mx-auto mb-3" />
          <p className="text-[#8A93A6] text-sm">
            {projects.length === 0 ? 'No projects yet — create one to get started.' : 'No projects match your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="project-grid">
          {displayProjects.map((project) => (
            <Card
              key={project.id}
              className="glass border-white/5 hover:bg-white/5 transition-all cursor-pointer group"
              onClick={() => handleOpenProject(project.id)}
              data-testid={`project-card-${project.id}`}
            >
              <CardContent className="pt-5 pb-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                      <FolderOpen className="h-4 w-4 text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-medium group-hover:text-blue-300 transition-colors truncate">
                        {project.name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge variant="outline" className="border-white/10 text-[#8A93A6] text-xs capitalize">
                          {project.status}
                        </Badge>
                        {project.archived && <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30 text-xs">Archived</Badge>}
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A93A6] shrink-0" onClick={(e) => e.stopPropagation()} data-testid={`project-card-menu-${project.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#14171F] border-white/10" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => openRename(project)} data-testid={`project-card-rename-${project.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-2" />Rename Project
                      </DropdownMenuItem>
                      {project.archived ? (
                        <DropdownMenuItem onClick={() => toggleArchive(project, false)} data-testid={`project-card-restore-${project.id}`}>
                          <ArchiveRestore className="h-3.5 w-3.5 mr-2" />Restore Project
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => toggleArchive(project, true)} data-testid={`project-card-archive-${project.id}`}>
                          <Archive className="h-3.5 w-3.5 mr-2" />Archive Project
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => setDeleteTarget(project)} className="text-red-400" data-testid={`project-card-delete-${project.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-2" />Delete Project
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex items-center justify-between text-xs text-[#8A93A6] pt-1">
                  <span className="flex items-center gap-1.5">
                    <FileVideo className="h-3.5 w-3.5" />
                    {project.parent_asset_count || 0} {project.parent_asset_count === 1 ? 'Asset' : 'Assets'}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : ''}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Project dialog — shared form, now with live Production Tools */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!creating) { setShowCreateDialog(open); if (!open) setNewProjectName(''); } }}>
        <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="new-project-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Project</DialogTitle>
          </DialogHeader>
          <ProjectFormDemo
            mode="create"
            name={newProjectName}
            onNameChange={setNewProjectName}
            onSubmit={() => handleCreateProject()}
            submitting={creating}
            submitDisabled={!newProjectName.trim()}
            actions={newProjectToolsActions}
          />
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(v) => !v && setRenameTarget(null)}>
        <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-sm" data-testid="rename-project-dialog">
          <DialogHeader><DialogTitle className="text-white">Rename Project</DialogTitle></DialogHeader>
          <Input value={renameValue} onChange={e => setRenameValue(e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="rename-project-input" />
          <DialogFooter>
            <Button variant="outline" className="border-white/10" onClick={() => setRenameTarget(null)} data-testid="rename-project-cancel">Cancel</Button>
            <Button className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" onClick={confirmRename} data-testid="rename-project-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#14171F] border-white/10" data-testid="delete-project-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A93A6]">This hides it from your Workspace (demo only) — it is not removed from the server.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-white" data-testid="delete-project-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="delete-project-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
